import { FlatList, Image, StyleSheet, View, Pressable, useWindowDimensions } from "react-native";
import { FindifyTopBarOverlay } from "../../../genericPages/components/FindifyTopBarOverlay";
import React, { useContext, useEffect, useState } from "react";
import { PlaceColors } from "../../../genericPages/helpers/PlaceColors";
import { BasePage } from "../../../genericPages/helpers/BasePage";
import { useTranslation } from "react-i18next";
import { FindifySimpleHeader, SimpleHeaderAction } from "../../../genericPages/components/FindifySimpleHeader";
import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { makeIconFilterButton } from "../../../genericPages/helpers/Components";
import { FindifyBasicModal } from "../../../genericPages/components/FindifyBasicModal";
import { PlaceText } from "../../../genericPages/components/PlaceText";
import { BaseFindifyList } from "../../../genericPages/components/FindifyList";
import { getTranslatedString } from "../../../genericPages/helpers/FindifyTranslatedString";
import { PlaceListInitialUseEffect, PlaceListOnSelectedCategoryChangeEffect, loadContentsPageFromApi } from "@/layouts/genericPages/helpers/PlaceListHelper";
import { getContentIdForModule, getFeaturedImageUrl, makeContentDate, makeContentTitle } from "@/layouts/genericPages/helpers/DetailsHelpers";
import Icon from "react-native-vector-icons/Ionicons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { BiscainhosFooterLogo, BottomNavigationPadding } from "../../../genericPages/components/FindifyNavigation";
import { BiscainhosAppHeader } from "../../../genericPages/components/BiscainhosAppHeader";
import { useSeoTitle } from "../../../genericPages/helpers/SeoTitle";
import { getWorkspaceTitle } from "../../../genericPages/helpers/WorkspaceText";

const isEnglishLanguage = (language?: string) => language?.startsWith('en');
const isFrenchLanguage = (language?: string) => language?.startsWith('fr');
const localizedText = (findifyDataContext: any, pt: string, en: string, fr?: string) => {
    if (isFrenchLanguage(findifyDataContext?.language)) return fr ?? en;
    return isEnglishLanguage(findifyDataContext?.language) ? en : pt;
};

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

const getCategoryIds = (content: any): number[] => (content?.categories ?? [])
    .map((line: any) => Number(line?.category_id ?? line?.category?.category_id ?? line))
    .filter(Number.isFinite);

export const PlaceContentListTab = ({ navigation, ...props }: any) => {
    const [contents, setContents] = useState([]);
    const { t } = useTranslation();
    const [module, setModule] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const findifyDataContext = useContext(FindifyDataContext);
    const [loaded, setLoaded] = useState(false);
    const [categoryFiltersModalOpen, setCategoriesModalOpen] = useState(false);
    const [availableModuleCategories, setAvailableModuleCategories] = useState<any[]>([]);
    const [actionsList, setActionsList] = useState<SimpleHeaderAction[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const insets = useSafeAreaInsets();
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;

    useEffect(() => {
        PlaceListInitialUseEffect({
            props,
            setContents,
            setLoaded,
            setModule,
            setSelectedCategories,
            findifyDataContext,
            setAvailableModuleCategories,
            contents,
        });
    }, [])


    useEffect(() => {
        setHasMore(true);
        PlaceListOnSelectedCategoryChangeEffect({
            loaded,
            selectedCategories,
            setContents,
            module,
            findifyDataContext,
            setActionsList,
            availableModuleCategories,
            t,
        });
    }, [selectedCategories])

    const loadMore = async () => {
        if (!loaded || !hasMore || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const moreResultsAvailable = await loadContentsPageFromApi(
                selectedCategories,
                contents,
                module,
                findifyDataContext,
                setContents,
            );
            setHasMore(moreResultsAvailable);
        } finally {
            setIsLoadingMore(false);
        }
    }


    const header = <FindifySimpleHeader navigation={navigation}
        // module if no category filter or multiple category filter, single cat filter shows cat title
        title={
            selectedCategories.length === 1 ?
                getTranslatedString(
                    availableModuleCategories.find((cat: any) => cat.category_id === selectedCategories[0])?.name, findifyDataContext) :
                t('modules.content_types.' + module)
        }
        actions={actionsList}
        image={findifyDataContext?.workspace.app_settings?.app_banner_url} />;

    const openContent = (content: any) => {
        const contentId = getContentIdForModule(content, module);
        navigation.push('PlaceContentDetails', {
            module: module,
            content_id: contentId,
            page_slug: content.page_slug
        });
    }

    const isBiscainhosPoiList = Number(findifyDataContext?.workspace?.workspace_id) === 155 && module === 'points_of_interest';
    const isBiscainhosContentList = Number(findifyDataContext?.workspace?.workspace_id) === 155 && module === 'contents';
    const isBiscainhosStyledList = isBiscainhosPoiList || isBiscainhosContentList;
    const selectedCategory = selectedCategories.length === 1
        ? availableModuleCategories.find((cat: any) => cat.category_id === selectedCategories[0])
        : undefined;
    const listTitle = selectedCategory
        ? getTranslatedString(selectedCategory.name, findifyDataContext)
        : isBiscainhosContentList
            ? localizedText(findifyDataContext, 'Curiosidades', 'Curiosities', 'Curiosités')
            : localizedText(findifyDataContext, 'Pontos de Interesse', 'Points of Interest', "Points d'intérêt");
    useSeoTitle(listTitle || t('modules.content_types.' + module), getWorkspaceTitle(findifyDataContext));
    const heroImage = getFeaturedImageUrl(selectedCategory) || findifyDataContext?.workspace?.app_settings?.app_banner_url;
    const heroDescription = stripHtml(getTranslatedString(selectedCategory?.description, findifyDataContext));
    const colors = PlaceColors(findifyDataContext);
    const contentCategoryIds = new Set(contents.flatMap(getCategoryIds));
    const hasMultipleContentCategories = contentCategoryIds.size > 1;
    // Curiosities are often configured under a single category. In that case
    // filtering cannot change the list, so do not show a redundant control.
    const shouldShowCategoryFilter = isBiscainhosContentList
        ? hasMultipleContentCategories
        : availableModuleCategories.length > 1;

    if (isBiscainhosStyledList) {
        return <BasePage navigation={navigation}>
            <View style={[styles.biscainhosListPage, { backgroundColor: colors.background.get() }]}>
                <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} allowBackFallback={false} />
                <FlatList
                    key={isLandscapeTablet ? 'biscainhos-poi-grid' : 'biscainhos-poi-list'}
                    data={contents}
                    numColumns={isLandscapeTablet ? 2 : 1}
                    columnWrapperStyle={isLandscapeTablet ? styles.biscainhosPoiGridRow : undefined}
                    keyExtractor={(item: any, index: number) => `${getContentIdForModule(item, module)}-${index}`}
                    showsVerticalScrollIndicator={false}
                    onEndReached={() => {
                        loadMore();
                    }}
                    onEndReachedThreshold={0.7}
                    contentContainerStyle={{
                        width: '100%',
                        maxWidth: isLandscapeTablet ? 1120 : isTablet ? 760 : undefined,
                        alignSelf: 'center',
                        paddingHorizontal: isTablet ? 32 : 20,
                        paddingTop: isTablet ? 56 : 48,
                        paddingBottom: insets.bottom + BottomNavigationPadding + 24,
                    }}
                    ListHeaderComponent={() => (
                        <View style={styles.biscainhosHeroCard}>
                            {heroImage ? <Image source={{ uri: heroImage }} resizeMode="cover" style={styles.biscainhosHeroImage} /> : null}
                            <View style={styles.biscainhosHeroOverlay} />
                            <View style={styles.biscainhosHeroGradient} />
                            <View style={styles.biscainhosHeroText}>
                                <PlaceText style={[styles.biscainhosHeroEyebrow, { color: colors.secondary.get() }]}>
                                    {heroDescription}
                                </PlaceText>
                                <PlaceText style={styles.biscainhosHeroTitle}>{listTitle}</PlaceText>
                            </View>
                        </View>
                    )}
                    ListFooterComponent={() => <BiscainhosFooterLogo findifyDataContext={findifyDataContext} />}
                    renderItem={({ item, index }: any) => {
                        const image = getFeaturedImageUrl(item);
                        const title = makeContentTitle(item, module, findifyDataContext);
                        const resume = stripHtml(getTranslatedString(item.resume || item.description, findifyDataContext));
                        const isFeaturedCard = !isLandscapeTablet && (index === 0 || index === 3);
                        return <Pressable
                            style={[
                                styles.biscainhosPoiCard,
                                isLandscapeTablet ? styles.biscainhosPoiCardGrid : null,
                                {
                                    backgroundColor: colors.background.solidAlphaWhiteBase(55),
                                    borderColor: colors.secondary.withAlpha(65),
                                },
                                isFeaturedCard ? styles.biscainhosPoiCardFeatured : null
                            ]}
                            onPress={() => openContent(item)}>
                            {image ? <Image source={{ uri: image }} resizeMode="cover" style={[styles.biscainhosPoiImage, isFeaturedCard ? styles.biscainhosPoiImageFeatured : null]} /> : null}
                            <View style={styles.biscainhosPoiBody}>
                                <PlaceText style={[styles.biscainhosPoiTitle, { color: colors.text.get() }]}>{title}</PlaceText>
                                {!!resume && <PlaceText numberOfLines={3} style={[styles.biscainhosPoiResume, { color: colors.text.withAlpha(190) }]}>{resume}</PlaceText>}
                                <View style={styles.biscainhosReadMoreRow}>
                                    <PlaceText style={[
                                        styles.biscainhosReadMore,
                                        {
                                            color: colors.primary.get(),
                                            borderBottomColor: colors.primary.withAlpha(80),
                                        }
                                    ]}>{localizedText(findifyDataContext, 'LER MAIS', 'READ MORE', 'LIRE PLUS')}</PlaceText>
                                </View>
                            </View>
                        </Pressable>
                    }}
                />
                {shouldShowCategoryFilter ? <>
                    <Pressable style={[styles.biscainhosFilterButton, { bottom: insets.bottom + BottomNavigationPadding + 12, backgroundColor: colors.primary.get() }]} onPress={() => setCategoriesModalOpen(true)}>
                        <Icon name="funnel" size={20} color={colors.background.get()} />
                    </Pressable>
                    <FindifyBasicModal
                    isOpen={categoryFiltersModalOpen}
                    closeModal={() => {
                        setCategoriesModalOpen(false)
                    }}
                    style={{
                        width: '92%',
                        maxWidth: 440,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: colors.primary.withAlpha(45),
                        backgroundColor: colors.background.get(),
                        paddingHorizontal: 18,
                        paddingTop: 10,
                        paddingBottom: 18,
                        alignItems: 'stretch',
                    }}
                >
                        <View style={styles.biscainhosFilterModal}>
                            <View style={[styles.biscainhosFilterHandle, { backgroundColor: colors.primary.withAlpha(55) }]} />
                            <View style={styles.biscainhosFilterHeader}>
                                <View>
                                <PlaceText style={[styles.biscainhosFilterTitle, { color: colors.primary.get() }]}>
                                    {isBiscainhosContentList
                                        ? localizedText(findifyDataContext, 'Filtrar conteúdos', 'Filter stories', 'Filtrer les contenus')
                                        : localizedText(findifyDataContext, 'Filtrar pontos', 'Filter places', 'Filtrer les lieux')}
                                </PlaceText>
                                <PlaceText style={[styles.biscainhosFilterSubtitle, { color: colors.text.withAlpha(190) }]}>{localizedText(findifyDataContext, 'Escolha uma ou mais categorias', 'Choose one or more categories', 'Choisissez une ou plusieurs catégories')}</PlaceText>
                            </View>
                            <Pressable style={[styles.biscainhosFilterClose, { backgroundColor: colors.primary.withAlpha(15) }]} onPress={() => setCategoriesModalOpen(false)}>
                                <Icon name="close-outline" size={26} color={colors.primary.get()} />
                            </Pressable>
                        </View>
                        {availableModuleCategories.map((cat: any, index: number) => {
                            const selected = selectedCategories.includes(cat.category_id);
                            return <Pressable
                                key={'avaliableLanguages' + index}
                                style={[
                                    styles.biscainhosFilterOption,
                                    {
                                        backgroundColor: colors.background.solidAlphaWhiteBase(35),
                                        borderColor: colors.primary.withAlpha(45),
                                    },
                                    selected ? {
                                        backgroundColor: colors.secondary.withAlpha(55),
                                        borderColor: colors.primary.get(),
                                    } : null
                                ]}
                                onPress={() => {
                                    if (selected) {
                                        setSelectedCategories(selectedCategories.filter((c: number) => c !== cat.category_id));
                                    } else {
                                        setSelectedCategories([...selectedCategories, cat.category_id]);
                                    }
                                }}>
                                <PlaceText style={[
                                    styles.biscainhosFilterOptionText,
                                    { color: selected ? colors.primary.get() : colors.text.get() },
                                ]}>{getTranslatedString(cat.name, findifyDataContext)}</PlaceText>
                                <Icon name={selected ? "checkmark-circle" : "ellipse-outline"} size={22} color={selected ? colors.primary.get() : colors.primary.withAlpha(70)} />
                            </Pressable>
                        })}
                        <View style={styles.biscainhosFilterActions}>
                            <Pressable
                                style={[styles.biscainhosFilterClearButton, { borderColor: colors.primary.get() }]}
                                onPress={() => setSelectedCategories([])}>
                                <PlaceText style={[styles.biscainhosFilterClearText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Limpar', 'Clear', 'Effacer')}</PlaceText>
                            </Pressable>
                            <Pressable
                                style={[styles.biscainhosFilterApplyButton, { backgroundColor: colors.primary.get() }]}
                                onPress={() => setCategoriesModalOpen(false)}>
                                <PlaceText style={[styles.biscainhosFilterApplyText, { color: colors.background.get() }]}>{localizedText(findifyDataContext, 'Aplicar', 'Apply', 'Appliquer')}</PlaceText>
                            </Pressable>
                        </View>
                    </View>
                    </FindifyBasicModal>
                </> : null}
            </View>
        </BasePage>
    }

    return <BasePage navigation={navigation}>
        <FindifyTopBarOverlay />
        <BaseFindifyList
            items={contents.map((content: any) => {
                const contentId = getContentIdForModule(content, module);
                return {
                    id: contentId,
                    image: getFeaturedImageUrl(content),
                    title: makeContentTitle(content, module, findifyDataContext),
                    resume: getTranslatedString(content.resume, findifyDataContext),
                    date: makeContentDate(content, module),
                    onPress: () => {
                        navigation.push('PlaceContentDetails', {
                            module: module,
                            content_id: contentId,
                            page_slug: content.page_slug
                        })
                    }
                }
            })}
            header={header}
            onEndReached={() => {
                loadMore();
            }}
            enableGrid={true}
            selectLabel={t('list.read_more')}
            actions={shouldShowCategoryFilter ? <>
                {makeIconFilterButton(() => setCategoriesModalOpen(true), "funnel-outline", selectedCategories.length !== 0, findifyDataContext)}
            </> : null}
        />

        {shouldShowCategoryFilter ? <FindifyBasicModal
            isOpen={categoryFiltersModalOpen}
            closeModal={() => {
                setCategoriesModalOpen(false)
            }}
        >
            <View style={styles.optionsZoneOptionsModal}>
                {availableModuleCategories.map((cat: any, index: number) => {
                    return <Pressable
                        key={'avaliableLanguages' + index}
                        onPress={() => {
                            // findifyDataContext?.setLanguage(lang.tag);
                            if (selectedCategories.includes(cat.category_id)) {
                                setSelectedCategories(selectedCategories.filter((c: number) => c !== cat.category_id));
                            } else {
                                setSelectedCategories([...selectedCategories, cat.category_id]);
                            }

                        }}>
                        <View style={{
                            ...styles.optionsZoneOptionModalLine,
                            ...(index !== 0 ? styles.optionTopBorder : {}),
                            ...(selectedCategories.includes(cat.category_id) ? { opacity: 0.6 } : {})
                        }}>
                            <PlaceText style={{ color: PlaceColors(findifyDataContext).text.get() }} >{getTranslatedString(cat.name, findifyDataContext)}</PlaceText>
                        </View>
                    </Pressable>
                })}
            </View>
        </FindifyBasicModal> : null}
    </BasePage >;
};


const styles = StyleSheet.create({

    container: {
        height: '100%',
        width: '100%',
    },
    map: {
        flex: 1
    },
    optionTopBorder: {
        borderTopColor: "#00000060",
        borderTopWidth: 1
    },
    optionsZoneOptionsModal: {

    },
    optionsZoneOptionModalLine: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 15,
        padding: 10,
        // width: '90%'

    },
    optionsZoneDivider: {
        width: '80%',
        marginLeft: '10%',
        marginRight: '10%',
        marginTop: 10,
        height: 2,
        borderRadius: 6,
        backgroundColor: '#00000030'
    },
    biscainhosListPage: {
        flex: 1,
    },
    biscainhosListHeader: {
        height: 62,
        paddingHorizontal: 24,
        borderBottomWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    biscainhosHeaderIconButton: {
        width: 42,
        height: 42,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosHeaderLogo: {
        width: 154,
        height: 38,
    },
    biscainhosHeaderActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    biscainhosAccessibilityButton: {
        width: 42,
        height: 42,
        borderRadius: 11,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosHeroCard: {
        height: 400,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 48,
        backgroundColor: '#111',
    },
    biscainhosHeroImage: {
        width: '100%',
        height: '100%',
    },
    biscainhosHeroOverlay: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.18)',
    },
    biscainhosHeroGradient: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '55%' as any,
        backgroundColor: 'rgba(0,0,0,0.46)',
    },
    biscainhosHeroText: {
        position: 'absolute',
        left: 28,
        right: 24,
        bottom: 30,
    },
    biscainhosHeroEyebrow: {
        fontSize: 11,
        lineHeight: 16,
        letterSpacing: 1.6,
        fontWeight: '700',
        marginBottom: 8,
    },
    biscainhosHeroTitle: {
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 46,
        lineHeight: 52,
        fontWeight: '700',
    },
    biscainhosPoiCard: {
        borderWidth: 1,
        borderRadius: 8,
        marginBottom: 24,
        overflow: 'hidden',
    },
    biscainhosPoiGridRow: {
        gap: 24,
        alignItems: 'stretch',
    },
    biscainhosPoiCardGrid: {
        flex: 1,
        minWidth: 0,
    },
    biscainhosPoiCardFeatured: {
        marginBottom: 28,
    },
    biscainhosPoiImage: {
        width: '100%',
        height: 260,
    },
    biscainhosPoiImageFeatured: {
        height: 300,
    },
    biscainhosPoiBody: {
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 20,
        minHeight: 150,
    },
    biscainhosPoiTitle: {
        fontFamily: 'Georgia',
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '700',
        marginBottom: 12,
    },
    biscainhosPoiResume: {
        fontSize: 14,
        lineHeight: 20,
    },
    biscainhosReadMoreRow: {
        alignItems: 'flex-end',
        marginTop: 20,
    },
    biscainhosReadMore: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: '700',
        letterSpacing: 1.1,
        borderBottomWidth: 1,
        paddingBottom: 2,
    },
    biscainhosFilterButton: {
        position: 'absolute',
        right: 24,
        width: 56,
        height: 56,
        borderRadius: 28,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.22,
        shadowRadius: 8,
        elevation: 8,
    },
    biscainhosFilterModal: {
        width: '100%',
    },
    biscainhosFilterHandle: {
        width: 46,
        height: 4,
        borderRadius: 4,
        alignSelf: 'center',
        marginBottom: 16,
    },
    biscainhosFilterHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 16,
    },
    biscainhosFilterTitle: {
        fontFamily: 'Georgia',
        fontSize: 25,
        lineHeight: 30,
        fontWeight: '700',
    },
    biscainhosFilterSubtitle: {
        fontSize: 12,
        lineHeight: 18,
        marginTop: 4,
    },
    biscainhosFilterClose: {
        width: 38,
        height: 38,
        borderRadius: 19,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosFilterOption: {
        minHeight: 48,
        borderRadius: 7,
        borderWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 10,
    },
    biscainhosFilterOptionSelected: {
    },
    biscainhosFilterOptionText: {
        fontSize: 14,
        lineHeight: 19,
        fontWeight: '600',
        flex: 1,
        paddingRight: 10,
    },
    biscainhosFilterOptionTextSelected: {},
    biscainhosFilterActions: {
        flexDirection: 'row',
        gap: 10,
        marginTop: 8,
    },
    biscainhosFilterClearButton: {
        flex: 1,
        minHeight: 48,
        borderRadius: 6,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosFilterClearText: {
        fontSize: 14,
        fontWeight: '700',
    },
    biscainhosFilterApplyButton: {
        flex: 1,
        minHeight: 48,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosFilterApplyText: {
        fontSize: 14,
        fontWeight: '700',
    }
})
